import { AppletHostError } from '../applet-host-error';
import { AppletHostErrorCode } from '../applet-host-error-code';
import ac from 'argument-contracts';
import { logger } from '../logger';
import { DEFAULT_NAMESPACE } from '../default-namespace';

const TO_STRING_RADIX = 36;
const REGISTRATION_ID_DELIMITER = '___';
function buildId() {
    return `${Date.now()}${REGISTRATION_ID_DELIMITER}${Math.random().toString(TO_STRING_RADIX)}`;
}

/**
 * @callback isValidNamespaceCallback
 * @param {string} namespace - The namespace to test for validity
 * @returns {boolean} - Whether the given namespace should be considered valid
 */

/**
 * @async
 * @callback AppletHostSettingsChangeEventRegistrar~callback
 * @param {object} options - SEE BELOW
 * @param {string} options.namespace - The namespace for which the callbacks is being fired
 * @returns {undefined}
 */

/**
 * An event bus for callbacks after settings have changed on the applet host
 * @param {object} options - SEE BELOW
 * @param {isValidNamespaceCallback} options.isValidNamespaceCallback - Callback to test if a given namespace is valid
 */
export class AppletHostSettingsChangeEventRegistrar {
    constructor({ isValidNamespaceCallback }) {
        ac.assertFunction(isValidNamespaceCallback, 'isValidNamespaceCallback');

        this.__registeredCallbacks = new Map();

        this.__isValidNamespaceCallback = isValidNamespaceCallback;
    }

    /**
     * Register a callback to be invoked when the AppletHost's settings change
     * @param {object} options - SEE BELOW
     * @param {AppletHostSettingsChangeEventRegistrar~callback} options.callback - The function to be invoked when the AppletHost's settings change
     * @param {string} [options.namespace] - The namespace to listen for changes on
     * @throws {AppletHostError} - Namespace must be valid {@link AppletHostErrorCode#UNKNOWN_NAMESPACE}
     * @returns {string} - A registration id used to unregister
     */
    register({ callback, namespace = DEFAULT_NAMESPACE }) {
        ac.assertFunction(callback, 'callback');

        if (!this.__isValidNamespaceCallback(namespace)) {
            throw new AppletHostError({
                code: AppletHostErrorCode.UNKNOWN_NAMESPACE,
                message: `${namespace} is not a valid namespace`
            });
        }

        if (!this.__registeredCallbacks.has(namespace)) {
            this.__registeredCallbacks.set(namespace, new Map());
        }

        const registrationId = buildId();
        this.__registeredCallbacks
            .get(namespace)
            .set(registrationId, callback);

        return registrationId;
    }

    /**
     * Unregister a callback so it will no longer be invoked
     * @param {string} registrationId - The id of the callback to be unregistered
     * @returns {undefined}
     */
    unregister(registrationId) {
        ac.assertString(registrationId, 'registrationId');

        this.__registeredCallbacks.forEach(namespaceCallbacks =>
            namespaceCallbacks.delete(registrationId)
        );
    }

    /**
     * Invoke all registered callbacks in parallel
     * @param {object} [options] - SEE BELOW
     * @param {string} [options.namespace] - The namespace to fire callbacks for
     * @throws {AppletHostError} - Namespace must be valid {@link AppletHostErrorCode#UNKNOWN_NAMESPACE}
     * @returns {Promise<void>} A promise that will resolve when all callbacks complete
     */
    async fire({ namespace = DEFAULT_NAMESPACE } = {}) {
        if (!this.__isValidNamespaceCallback(namespace)) {
            throw new AppletHostError({
                code: AppletHostErrorCode.UNKNOWN_NAMESPACE,
                message: `${namespace} is not a valid namespace`
            });
        }

        if (this.__registeredCallbacks.has(namespace)) {
            const namespaceRegistrations = this.__registeredCallbacks.get(namespace);
            const namespaceCallbacks = Array.from(namespaceRegistrations.values());

            return Promise.all(namespaceCallbacks.map(async callback => {
                try {
                    await callback({ namespace });
                } catch (error) {
                    logger.error(error);
                }
            }));
        }
    }
}
